import api from './api';

export type CashRegisterStatus = 'open' | 'closed';
export type CloseType = 'partial' | 'final';

export interface CashRegisterUser {
  id: number;
  full_name: string;
  email: string;
}

export interface CashRegister {
  id: number;
  tenant_id: number;
  status: CashRegisterStatus;
  shift_number: number;  // Número de turno del día
  opened_at: string;
  opened_by_user_id: number;
  opened_by?: CashRegisterUser;
  opening_cash: number;
  opening_notes?: string;

  closed_at?: string;
  closed_by_user_id?: number;
  closed_by?: CashRegisterUser;
  closing_cash?: number;
  closing_notes?: string;
  close_type?: CloseType;  // "partial" o "final"
  auto_closed?: boolean;

  expected_cash?: number;
  cash_difference?: number;

  total_sales: number;
  total_cash_sales: number;
  total_card_sales: number;
  total_transfer_sales: number;
  total_qr_sales: number;
  total_withdrawals: number;  // Total de salidas de caja
  total_cash_withdrawals: number;  // Salidas de efectivo
  total_card_withdrawals: number;  // Salidas de tarjeta
  total_transfer_withdrawals: number;  // Salidas de transferencia

  total_cost: number;
  total_expenses: number;  // Gastos operativos (afectan ganancia)
  total_profit: number;

  created_at: string;
}

// === Salidas de Caja ===

export type CashWithdrawalCategory = 'delivery' | 'transport' | 'supplies' | 'services' | 'withdrawal' | 'supplier_payment' | 'salary' | 'other';
export type PaymentMethod = 'cash' | 'card' | 'transfer';

export interface CashWithdrawal {
  id: number;
  tenant_id: number;
  cash_register_id: number;
  amount: number;
  category: CashWithdrawalCategory;
  payment_method: PaymentMethod;
  description: string;
  recipient?: string;
  created_by_user_id: number;
  created_by?: { id: number; full_name: string };
  created_at: string;
}

export interface CashWithdrawalCreate {
  amount: number;
  category: CashWithdrawalCategory;
  payment_method?: PaymentMethod;
  description?: string;
  recipient?: string;
}

export interface CashRegisterSummary {
  id: number;
  status: CashRegisterStatus;
  shift_number: number;
  opened_at: string;
  closed_at?: string;
  opening_cash: number;
  closing_cash?: number;
  total_sales: number;
  total_profit: number;
  cash_difference?: number;
  opened_by?: CashRegisterUser;
  close_type?: CloseType;
  auto_closed?: boolean;
}

export interface CurrentCashRegisterStatus {
  is_open: boolean;
  current_register?: CashRegister;
  last_closed_register?: CashRegisterSummary;
  // Info para turnos
  last_partial_close_today?: CashRegisterSummary;  // Último cierre parcial de hoy
  can_open_new_shift: boolean;  // True si se puede abrir nuevo turno
  final_close_today?: boolean;  // El día ya tiene cierre final (el admin puede abrir igual)
  next_shift_number: number;  // Número del próximo turno
}

export interface CashRegisterCreate {
  opening_cash: number;
  opening_notes?: string;
  // Abrir turno aunque el día ya tenga cierre final (solo admin, para corregir
  // el caso de haber elegido "final" en lugar de "parcial").
  force_after_final?: boolean;
}

export interface CashRegisterClose {
  closing_cash: number;
  closing_notes?: string;
  close_type?: CloseType;  // "partial" para cierre de turno, "final" para cierre del día
}

export interface DailyProfit {
  date: string;
  total_sales: number;
  total_cost: number;
  total_expenses: number;
  total_profit: number;
  cash_difference: number;
}

export interface CashRegisterFilters {
  status?: CashRegisterStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export const cashRegisterService = {
  getStatus: async (): Promise<CurrentCashRegisterStatus> => {
    const response = await api.get('/cash-register/status');
    return response.data;
  },

  getRegisters: async (filters?: CashRegisterFilters): Promise<CashRegisterSummary[]> => {
    const response = await api.get('/cash-register/', { params: filters });
    return response.data;
  },

  getRegister: async (id: number): Promise<CashRegister> => {
    const response = await api.get(`/cash-register/${id}`);
    return response.data;
  },

  openRegister: async (data: CashRegisterCreate): Promise<CashRegister> => {
    const response = await api.post('/cash-register/open', data);
    return response.data;
  },

  closeRegister: async (id: number, data: CashRegisterClose): Promise<CashRegister> => {
    const response = await api.post(`/cash-register/${id}/close`, data);
    return response.data;
  },

  reopenRegister: async (id: number): Promise<CashRegister> => {
    const response = await api.post(`/cash-register/${id}/reopen`);
    return response.data;
  },

  getProfitHistory: async (days: number = 30): Promise<DailyProfit[]> => {
    const response = await api.get('/cash-register/history/profit', { params: { days } });
    return response.data;
  },

  // === Salidas de Caja ===

  getWithdrawals: async (cashRegisterId?: number): Promise<CashWithdrawal[]> => {
    const response = await api.get('/cash-register/withdrawals/', {
      params: cashRegisterId ? { cash_register_id: cashRegisterId } : undefined
    });
    return response.data;
  },

  createWithdrawal: async (data: CashWithdrawalCreate): Promise<CashWithdrawal> => {
    const response = await api.post('/cash-register/withdrawals/', data);
    return response.data;
  },

  deleteWithdrawal: async (id: number): Promise<void> => {
    await api.delete(`/cash-register/withdrawals/${id}`);
  },
};

// Labels para categorías de salidas
export const WITHDRAWAL_CATEGORY_LABELS: Record<CashWithdrawalCategory, string> = {
  delivery: 'Cadete/Envío',
  transport: 'Transporte',
  supplies: 'Compras/Insumos',
  services: 'Servicios',
  withdrawal: 'Retiro de Dinero',
  supplier_payment: 'Pago a Proveedor',
  salary: 'Sueldo',
  other: 'Otro',
};

// Labels para métodos de pago
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
};
